import { environment } from '../../environments/environment';
import * as moment from 'moment';
import { EStopLightColor } from './global.models';
import { HttpClient } from '@angular/common/http';
import {
	ChartBrowserStore,
	GraphElement,
	GraphGroupElement
} from '../view-shared/chart-browser/chart-browser.models';
import { TranslateLoader } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { IFlagType } from '../view-routes/inside/inside.models';
/**
 * Global formats & constants
 * More notes...
 *
 */
export class Constants {
	// increment to force logout
	static readonly version = 16215;

	static readonly StopLightColorsMap = new Map<EStopLightColor, string>([
		[EStopLightColor.DANGER, '#C1292E'],
		[EStopLightColor.WARNING, '#F8C630'],
		[EStopLightColor.OK, '#6DA34D'],
		[EStopLightColor.IDLE, '#0D79F2'],
		[EStopLightColor.UNKNOWN, 'grey']
	]);

	static readonly markerVirtualSvgs: string[] = [
		'marker-virtual',
		'marker-virtual-selectindicator',
		'marker-icon-radar'
	];
	static readonly markerGenericSvgs: string[] = [
		'marker-generic',
		'marker-generic-selectindicator',
		'marker-pixel',
		'marker-pixel-selectindicator',
		'marker-cluster',
		'marker-cluster-selectindicator'
	];
	static readonly markerGenericIconSvgs: string[] = [
		'marker-icon-dryp',
		'marker-icon-flow',
		'marker-icon-model',
		'marker-icon-nondryp',
		'marker-icon-none',
		'marker-icon-other',
		'marker-icon-rain',
		'marker-icon-warning',
		'marker-icon-waterlevel',
		'marker-icon-worldsearch'
	];

	static createTranslateModuleConfig(path: string) {
		return {
			loader: {
				provide: TranslateLoader,
				useFactory: (http: HttpClient) =>
					new TranslateHttpLoader(http, path, `.json`),
				deps: [HttpClient]
			},
			isolate: false,
			extend: true
		};
	}

	static hex2rgba(hex: string, alpha: number = 1): string {
		if (!hex.includes('#')) {
			hex = '#000000';
		}
		const [r, g, b] = hex.match(/\w\w/g).map(x => parseInt(x, 16));
		return `rgba(${r},${g},${b},${alpha})`;
	}

	static pipeTimestamp(value: number | string, args: any[]): any {
		const format = args[0];
		const time = Constants.returnTimeStampAfterOffset(value as number, true);
		const dt: moment.Moment = moment(time).utc();
		return dt.format(format);
	}

	static returnTimeStampAfterOffset(timestamp: number, add?: boolean): number {
		const value: moment.Moment = moment(timestamp);
		const offset: number = value.utcOffset();
		if (add) {
			value.add(offset, 'minutes');
		} else {
			value.subtract(offset, 'minutes');
		}
		return value.valueOf();
	}

	static generateUniqueString(length: number): string {
		let result = '';
		const characters =
			'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
		const charactersLength = characters.length;
		for (let i = 0; i < length; i++) {
			result += characters.charAt(Math.floor(Math.random() * charactersLength));
		}
		return result;
	}

	static durationOutput(duration: number): string {
		const prefix = duration > 0 ? '+ ' : duration < 0 ? '- ' : '';
		const span = moment.duration(Math.abs(duration));
		let str: string = prefix;

		if (span.years()) {
			str = `${span.years()}y `;
		}
		if (span.months()) {
			str += `${span.months()}mo `;
		}
		if (span.days()) {
			str += `${span.days()}d `;
		}
		if (span.hours()) {
			str += `${span.hours()}h `;
		}
		if (span.minutes()) {
			str += `${span.minutes()}min `;
		}
		return str;
	}

	static graphGroupCleaner(
		pointIds: number[],
		templateStore: ChartBrowserStore,
		graphsRef: object
	): void {
		// Find graphs
		const pointlessGraphs: GraphElement[] = templateStore.graphList.filter(
			(g: GraphElement) => pointIds.includes(g.point.id)
		);

		// Trim graphGroups
		const ggBin: GraphGroupElement[] = [];
		templateStore.graphGroups.map((gg: GraphGroupElement) => {
			const graphs: GraphElement[] = gg.graphs.filter(
				(g: GraphElement) => !pointlessGraphs.includes(g)
			);
			if (graphs.length < 1 && !ggBin.includes(gg)) {
				ggBin.push(gg);
			} else {
				gg.graphs = graphs;
			}
		});
		if (ggBin.length > 0) {
			templateStore.graphGroups = templateStore.graphGroups.filter(
				(gg: GraphGroupElement) => !ggBin.includes(gg)
			);
		}

		// Kill & remove graphs
		pointlessGraphs.map((g: GraphElement) => {
			const id: string = `${g.row.id}-${g.sensorTypeId}`;
			const dataSub = (graphsRef[id] as GraphElement).dataSub;
			if (dataSub) {
				dataSub.unsubscribe();
			}
			const flagSub = (graphsRef[id] as GraphElement).flagSub;
			if (flagSub) {
				flagSub.unsubscribe();
			}
			delete graphsRef[id];
		});
		templateStore.graphList = templateStore.graphList.filter(
			(g: GraphElement) => !pointIds.includes(g.point.id)
		);
	}

	/** API endpoint */
	static readonly API = /localhost/.test(document.location.host)
		? ''
		: environment.endpoint;

	/** Rain API endpoint */
	static readonly RAIN_API = 'https://rain.dryp.live';

	static readonly FlagTypes: IFlagType[] = [
		{
			id: 1,
			color: '#000000',
			hierarchy: 2,
			key: 'oub',
			str: 'Out of bounds',
			systemFlag: true
		},
		{ id: 2, color: '#ff0000', hierarchy: 1, key: 'del', str: 'Delete data' },
		{ id: 3, color: '#6aaffd', hierarchy: 12, key: 'com', str: 'Comment only' },
		{ id: 4, color: '#4169e1', hierarchy: 6, key: 'cor', str: 'Correct data' },
		{ id: 5, color: '#ffa500', hierarchy: 8, key: 'ofs', str: 'Offset data' },
		{
			id: 6,
			color: '#800080',
			hierarchy: 9,
			key: 'drf',
			str: 'Drift adjustment'
		},
		{
			id: 7,
			color: '#000000',
			hierarchy: 5,
			key: 'mlc',
			str: 'Machine learning curated',
			systemFlag: true
		},
		{ id: 8, color: 'brown', hierarchy: 10, key: 'cxm', str: 'No modelling' },
		{
			id: 9,
			color: '#000000',
			hierarchy: 11,
			key: 'srv',
			str: 'Service on sensor',
			systemFlag: true
		},
		{
			id: 10,
			color: '#404040',
			hierarchy: 4,
			key: 'spk',
			str: 'Spikes in data',
			systemFlag: true
		},
		{
			id: 11,
			color: '#808080',
			hierarchy: 3,
			key: 'poub',
			str: 'Physical out of bounds',
			systemFlag: true
		},
		{
			id: 12,
			color: '#008000',
			hierarchy: 12,
			key: 'ok',
			str: 'Data inspected and approved',
			systemFlag: false
		}
	];

	static readonly FlagIndexes = {
		1: { ndx: 0 },
		2: { ndx: 1 },
		3: { ndx: 2 },
		4: { ndx: 3 },
		5: { ndx: 4 },
		6: { ndx: 5 },
		7: { ndx: 6 },
		8: { ndx: 7 },
		9: { ndx: 8 },
		10: { ndx: 9 },
		11: { ndx: 10 },
		12: { ndx: 11 }
	};

	static readonly GeoColors = [
		'blue',
		'blueviolet',
		'green',
		'yellow',
		'orange',
		'grey',
		'magenta',
		'greenyellow',
		'darkblue',
		'coral',
		'violet',
		'lightseagreen',
		'orangered',
		'darkgreen',
		'darkmagenta',
		'black',
		'darkorange'
	];

	static readonly defaultParameterIds: number[] = [
		16,
		1020,
		1098,
		1117,
		180,
		183,
		262,
		1053,
		1074,
		1101,
		1102,
		1103,
		1127,
		1129,
		1131,
		1132,
		1133,
		1134,
		1135,
		1136,
		1137,
		1138,
		1139,
		1143,
		1144,
		315,
		1019,
		1025,
		1107,
		1108,
		1109,
		1004,
		1118,
		189,
		310,
		400,
		402,
		1007,
		1086,
		1087,
		1088,
		316,
		19,
		111,
		305,
		314,
		1001,
		1008,
		1009,
		1060,
		1061,
		1062,
		1063,
		1064,
		1065,
		1066,
		1128,
		1130,
		263,
		269,
		1017,
		1035,
		1078,
		1079,
		1080,
		204,
		207,
		1069,
		1070,
		1090,
		190,
		309,
		1005,
		401,
		191,
		1091,
		1113,
		1125,
		1126,
		1140,
		1068,
		265,
		266,
		267,
		1041,
		1044,
		268,
		1021,
		302,
		322,
		1013,
		1014,
		320,
		321,
		1002,
		1040,
		1058,
		1010,
		1015,
		1067,
		1122,
		1123,
		1124,
		1141,
		1123,
		1106,
		1026,
		1042,
		1059,
		1075,
		1076,
		1166,
		1167,
		1168,
		1169
	];

	static readonly decimalsByParameterId: { [key: number]: number } = {
		1: 2,
		5: 2,
		16: 2,
		17: 2,
		19: 2,
		34: 2,
		40: 2,
		180: 2,
		183: 2,
		189: 2,
		190: 2,
		191: 2,
		204: 2,
		207: 2,
		214: 2,
		253: 1,
		262: 2,
		263: 2,
		265: 3,
		266: 2,
		267: 2,
		268: 1,
		269: 2,
		294: 3,
		296: 2,
		301: 2,
		302: 2,
		305: 2,
		306: 2,
		307: 2,
		308: 2,
		309: 2,
		310: 4,
		311: 2,
		312: 2,
		313: 2,
		314: 2,
		315: 2,
		316: 2,
		317: 2,
		318: 2,
		319: 2,
		320: 2,
		321: 2,
		322: 2,
		400: 3,
		401: 0,
		402: 3,
		1001: 2,
		1002: 2,
		1004: 1,
		1005: 2,
		1006: 0,
		1007: 2,
		1008: 2,
		1009: 1,
		1010: 2,
		1011: 2,
		1012: 0,
		1013: 2,
		1014: 2,
		1015: 2,
		1016: 0,
		1017: 0,
		1018: 2,
		1019: 2,
		1020: 2,
		1021: 2,
		1022: 2,
		1023: 4,
		1024: 3,
		1025: 1,
		1026: 1,
		1035: 0,
		1036: 2,
		1037: 1,
		1038: 1,
		1039: 1,
		1040: 1,
		1041: 2,
		1042: 0,
		1043: 2,
		1044: 1,
		1045: 0,
		1046: 0,
		1047: 2,
		1048: 0,
		1050: 0,
		1051: 1,
		1053: 1,
		1054: 1,
		1055: 3,
		1056: 3,
		1057: 4,
		1058: 2,
		1059: 2,
		1060: 2,
		1061: 2,
		1062: 2,
		1063: 2,
		1064: 2,
		1065: 2,
		1066: 2,
		1067: 4,
		1068: 2,
		1069: 2,
		1070: 2,
		1071: 2,
		1072: 0,
		1073: 0,
		1074: 2,
		1075: 2,
		1076: 2,
		1078: 2,
		1079: 2,
		1080: 0,
		1081: 0,
		1082: 0,
		1086: 2,
		1087: 2,
		1088: 2,
		1090: 2,
		1091: 2,
		1092: 0,
		1093: 0,
		1094: 2,
		1095: 2,
		1096: 2,
		1097: 0,
		1098: 2,
		1099: 0,
		1100: 2,
		1101: 2,
		1102: 2,
		1103: 2,
		1104: 2,
		1105: 2,
		1106: 2,
		1107: 2,
		1108: 2,
		1109: 2,
		1110: 2,
		1111: 0,
		1112: 2,
		1113: 1,
		1117: 2,
		1118: 2,
		1119: 3,
		1120: 3,
		1121: 3,
		1122: 6,
		1123: 6,
		1124: 6,
		1125: 1,
		1126: 2,
		1127: 2,
		1128: 2,
		1129: 2,
		1130: 2,
		1131: 2,
		1132: 2,
		1133: 2,
		1134: 2,
		1135: 2,
		1136: 2,
		1137: 2,
		1138: 2,
		1139: 2,
		1140: 2,
		1141: 2,
		1143: 2,
		1144: 2,
		1145: 2,
		1146: 2,
		1147: 3,
		1148: 2,
		1149: 0,
		1150: 2,
		1151: 2,
		1152: 1,
		1153: 1,
		1154: 1,
		1155: 2,
		1156: 2,
		1157: 2,
		1158: 2,
		1159: 2,
		1160: 2,
		1161: 2,
		1162: 2,
		1163: 2,
		1164: 2,
		1165: 2,
		1166: 2,
		1167: 2,
		1168: 2,
		1169: 2,
		1170: 2,
		1171: 2,
		1172: 1,
		1173: 3,
		1174: 1,
		1175: 2,
		1176: 2,
		1177: 2
		};
}
